// Cybersecurity.jsx
import React, { useState } from 'react';
import { 
  ShieldCheck, 
  Lock, 
  Globe, 
  Cpu, 
  Users, 
  AlertTriangle,
  CheckCircle,
  MessageSquare,
  Phone,
  Mail,
  Clock,
  Zap,
  Database,
  Network,
  Server,
  Search,
  Code,
  Terminal,
  Fingerprint,
  Eye,
  FileCheck,
  HardDrive,
  Key,
  Shield
} from 'lucide-react';

const Cybersecurity = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    message: ''
  });

  const services = [
    {
      icon: <ShieldCheck className="w-10 h-10 md:w-12 md:h-12" />,
      title: "Network Security",
      description: "Protect your network infrastructure from unauthorized access, misuse, or theft.",
      features: ["Firewall Management", "Intrusion Detection", "VPN Solutions", "Network Monitoring"]
    },
    {
      icon: <Lock className="w-10 h-10 md:w-12 md:h-12" />,
      title: "Data Protection",
      description: "Secure your sensitive data with advanced encryption and access control systems.",
      features: ["Data Encryption", "Access Control", "Backup Solutions", "Data Loss Prevention"]
    },
    {
      icon: <Globe className="w-10 h-10 md:w-12 md:h-12" />,
      title: "Web Security",
      description: "Protect web applications from attacks, vulnerabilities, and data breaches.",
      features: ["Web Application Firewall", "SSL Certificates", "DDoS Protection", "Vulnerability Scanning"]
    },
    {
      icon: <Users className="w-10 h-10 md:w-12 md:h-12" />,
      title: "Employee Training",
      description: "Educate your team on cybersecurity best practices and threat awareness.",
      features: ["Phishing Simulations", "Security Workshops", "Compliance Training", "Awareness Programs"]
    },
    {
      icon: <Cpu className="w-10 h-10 md:w-12 md:h-12" />,
      title: "Endpoint Security",
      description: "Secure all endpoints including mobile devices, laptops, and IoT devices.",
      features: ["Antivirus Solutions", "Device Management", "Mobile Security", "Patch Management"]
    },
    {
      icon: <AlertTriangle className="w-10 h-10 md:w-12 md:h-12" />,
      title: "Threat Intelligence",
      description: "Proactive threat detection and intelligence to prevent attacks before they happen.",
      features: ["Threat Monitoring", "Risk Assessment", "Incident Response", "24/7 SOC"]
    },
    {
      icon: <Code className="w-10 h-10 md:w-12 md:h-12" />,
      title: "Application Security",
      description: "Secure software development lifecycle and code-level protection.",
      features: ["Code Review", "Penetration Testing", "API Security", "SDLC Integration"]
    },
    {
      icon: <HardDrive className="w-10 h-10 md:w-12 md:h-12" />,
      title: "Cloud Security",
      description: "Protect cloud infrastructure, data, and applications across all platforms.",
      features: ["Cloud Compliance", "Configuration Management", "Data Sovereignty", "Identity Management"]
    }
  ];

  const securityFeatures = [
    {
      icon: <Fingerprint className="w-8 h-8" />,
      title: "Multi-Factor Authentication",
      description: "Add multiple layers of security to verify user identities"
    },
    {
      icon: <Eye className="w-8 h-8" />,
      title: "24/7 Monitoring",
      description: "Round-the-clock surveillance of your digital assets"
    },
    {
      icon: <FileCheck className="w-8 h-8" />,
      title: "Compliance Management",
      description: "Meet GDPR, HIPAA, PCI-DSS, and other regulatory requirements"
    },
    {
      icon: <Terminal className="w-8 h-8" />,
      title: "Incident Response",
      description: "Immediate action and recovery from security breaches"
    },
    {
      icon: <Key className="w-8 h-8" />,
      title: "Encryption Services",
      description: "End-to-end encryption for data at rest and in transit"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Zero Trust Architecture",
      description: "Never trust, always verify security model implementation"
    }
  ];

  const stats = [
    { value: "99.9%", label: "Uptime Guarantee", icon: <Server className="w-6 h-6 md:w-8 md:h-8" /> },
    { value: "24/7", label: "Security Monitoring", icon: <Clock className="w-6 h-6 md:w-8 md:h-8" /> },
    { value: "<30min", label: "Response Time", icon: <Zap className="w-6 h-6 md:w-8 md:h-8" /> },
    { value: "1000+", label: "Protected Clients", icon: <Users className="w-6 h-6 md:w-8 md:h-8" /> },
    { value: "50k+", label: "Threats Blocked", icon: <ShieldCheck className="w-6 h-6 md:w-8 md:h-8" /> },
    { value: "0%", label: "Major Breaches", icon: <AlertTriangle className="w-6 h-6 md:w-8 md:h-8" /> }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    alert('Thank you for your inquiry! Our security team will contact you shortly.');
    setFormData({
      name: '',
      email: '',
      company: '',
      service: '',
      message: ''
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* ===== HERO SECTION ===== */}
      <section className="relative py-12 md:py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>
        
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
             

              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                <span className="block text-gray-900">Fortify Your Digital</span>
                <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-red-600 via-gray-800 to-blue-600">
                  Security Frontier
                </span>
              </h1>

              <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed">
                Comprehensive cybersecurity solutions with North American accountability 
                and global execution excellence. Protect your business from evolving 
                digital threats with 24/7 monitoring and human-led security operations.
              </p>

             
            </div>
            
            <div className="relative">
              <div className="relative z-10 bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 md:p-8 border border-red-200 shadow-2xl shadow-red-500/10">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white p-4 md:p-6 rounded-xl border border-red-100">
                    <Network className="w-10 h-10 md:w-12 md:h-12 text-red-600 mb-4" />
                    <h3 className="font-bold text-base md:text-lg">Network Protection</h3>
                  </div>
                  <div className="bg-white p-4 md:p-6 rounded-xl border border-red-100">
                    <Database className="w-10 h-10 md:w-12 md:h-12 text-red-600 mb-4" />
                    <h3 className="font-bold text-base md:text-lg">Data Security</h3>
                  </div>
                  <div className="bg-white p-4 md:p-6 rounded-xl border border-red-100">
                    <ShieldCheck className="w-10 h-10 md:w-12 md:h-12 text-red-600 mb-4" />
                    <h3 className="font-bold text-base md:text-lg">Threat Defense</h3>
                  </div>
                  <div className="bg-white p-4 md:p-6 rounded-xl border border-red-100">
                    <Cpu className="w-10 h-10 md:w-12 md:h-12 text-red-600 mb-4" />
                    <h3 className="font-bold text-base md:text-lg">Endpoint Security</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== STATS SECTION ===== */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Trusted Security <span className="text-red-600">Metrics</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Proven results in protecting businesses from cyber threats
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white rounded-xl p-4 md:p-6 border border-gray-200 text-center hover:border-red-300 hover:shadow-lg transition-all">
                <div className="flex justify-center mb-3">
                  <div className="p-2 md:p-3 bg-red-50 rounded-lg">
                    {stat.icon}
                  </div>
                </div>
                <div className="text-xl md:text-2xl font-bold text-red-600 mb-1">{stat.value}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SERVICES SECTION ===== */}
      <section className="py-16 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12 md:mb-16">
            
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
              Comprehensive Cybersecurity <span className="text-red-600">Solutions</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              End-to-end security services tailored to protect your business at every layer
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {services.map((service, index) => (
              <div 
                key={index} 
                className="bg-white rounded-xl p-6 border border-gray-200 hover:border-red-500 transition-all duration-300 hover:shadow-xl group h-full"
              >
                <div className="mb-4 md:mb-6">
                  <div className="inline-flex p-3 rounded-lg bg-red-50 text-red-600 group-hover:bg-red-100 transition-all">
                    {service.icon}
                  </div>
                </div>
                <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-4 md:mb-6 text-sm md:text-base">{service.description}</p>
                <ul className="space-y-2 md:space-y-3">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-600 text-sm md:text-base">
                      <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-red-500 mr-2 md:mr-3 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECURITY FEATURES ===== */}
      <section className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
              Advanced Security <span className="text-red-600">Features</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Cutting-edge security technologies and methodologies for comprehensive protection
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {securityFeatures.map((feature, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border border-gray-200 hover:border-red-300 transition-all hover:shadow-lg">
                <div className="inline-flex p-3 rounded-lg bg-red-50 text-red-600 mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-900">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

     
    </div>
  );
};

export default Cybersecurity;